import {
  Modal, Form, Input, Select, Row, Col, DatePicker, Upload, message, Image, Typography, Button, Popconfirm,
} from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { clinicApi } from '../../../api/clinic.api';
import { referralApi } from '../../../api/referral.api';
import { useAuthStore } from '../../../store/auth.store';
import { usePermission } from '../../../hooks/usePermission';
import { getApiErrorMessage } from '../../../utils/helpers';

const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);
const phoneRule = {
  pattern: /^0?[1-9][0-9]{9}$/,
  message: 'Enter a valid 10-digit number, or 11 digits with a single leading 0',
};

const isImageFile = (file) => /\.(jpe?g|png)$/i.test(file.name || '') || /^image\//.test(file.type || '');

const MAX_TOTAL_ATTACHMENT_SIZE = 32 * 1024 * 1024; // 32MB combined across all attachments

const ReferralForm = ({ open, onClose, onSubmit, initialValues, loading }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const { user } = useAuthStore();
  const { isAdmin } = usePermission();
  const queryClient = useQueryClient();
  const isEdit = !!initialValues;
  const totalAttachmentSizeRef = useRef(0);
  const currentDoctorId = user?.doctor?.toString?.() || user?.doctor;
  // Patient documents here belong to the referring doctor — kept separate from the
  // clinic doctor's diagnosis/treatment report, which is submitted from the View page.
  const canManageAttachments = isEdit && (
    isAdmin
    || initialValues?.referringDoctor?.id === currentDoctorId || initialValues?.referringDoctor?._id === currentDoctorId
  );

  const addAttachmentsMutation = useMutation({
    mutationFn: (files) => referralApi.addAttachments(initialValues?.id, files),
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      queryClient.invalidateQueries(['referral', initialValues?.id]);
      queryClient.invalidateQueries(['referral-audit-logs', initialValues?.id]);
      message.success('Document(s) uploaded');
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const removeAttachmentMutation = useMutation({
    mutationFn: (attachmentPath) => referralApi.removeAttachment(initialValues?.id, attachmentPath),
    onSuccess: () => {
      queryClient.invalidateQueries(['referrals']);
      queryClient.invalidateQueries(['referral', initialValues?.id]);
      queryClient.invalidateQueries(['referral-audit-logs', initialValues?.id]);
      message.success('Document removed');
    },
    onError: (err) => message.error(getApiErrorMessage(err)),
  });

  const handleMissingDocUpload = (file, batch) => {
    const totalNewSize = batch.reduce((sum, f) => sum + f.size, 0);
    if (totalNewSize > MAX_TOTAL_ATTACHMENT_SIZE) {
      if (file === batch[0]) { message.error('Combined attachment size cannot exceed 32MB'); }
      return Upload.LIST_IGNORE;
    }
    if (file === batch[batch.length - 1]) {
      addAttachmentsMutation.mutate(batch);
    }
    return Upload.LIST_IGNORE;
  };

  const { data: clinicsData } = useQuery({
    queryKey: ['clinics-active'],
    queryFn: () => clinicApi.getActive(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    totalAttachmentSizeRef.current = 0;

    if (initialValues) {
      form.setFieldsValue({
        targetClinic: initialValues.targetClinic?.id || initialValues.targetClinic,
        referringDoctorName: initialValues.referringDoctor?.name || user?.name,
        referringDoctorContact: initialValues.referringDoctorContact,
        referringDoctorEmail: initialValues.referringDoctorEmail,
        childName: initialValues.childName,
        childDob: initialValues.childDob ? dayjs(initialValues.childDob) : undefined,
        parentName: initialValues.parentName,
        parentContact: initialValues.parentContact,
        parentEmail: initialValues.parentEmail,
        reason: initialValues.reason,
        preferredVisitTimeframe: initialValues.preferredVisitTimeframe,
      });
      setFileList((initialValues.attachmentUrls || []).map((url, idx) => ({
        uid: `existing-${idx}`,
        name: url.split('/').pop(),
        status: 'done',
        url,
        thumbUrl: isImageFile({ name: url }) ? url : undefined,
      })));
    } else {
      form.setFieldsValue({
        referringDoctorName: user?.name,
        referringDoctorContact: user?.phone,
        referringDoctorEmail: user?.email,
      });
    }
  }, [open, initialValues, form, user]);

  const beforeUpload = (file) => {
    // Uses a ref (not fileList state) so simultaneous multi-file selections are checked
    // against a running total — state updates are batched/async and would otherwise let
    // every file in the same selection see the same stale total and all pass individually.
    if (totalAttachmentSizeRef.current + file.size > MAX_TOTAL_ATTACHMENT_SIZE) {
      message.error('Combined attachment size cannot exceed 32MB');
      return Upload.LIST_IGNORE;
    }

    totalAttachmentSizeRef.current += file.size;
    const thumbUrl = isImageFile(file) ? URL.createObjectURL(file) : undefined;
    setFileList((prev) => [...prev, {
      uid: file.uid, name: file.name, status: 'done', originFileObj: file, url: thumbUrl, thumbUrl,
    }]);
    return Upload.LIST_IGNORE;
  };

  const handleRemove = (file) => {
    if (file.thumbUrl?.startsWith('blob:')) { URL.revokeObjectURL(file.thumbUrl); }
    totalAttachmentSizeRef.current = Math.max(0, totalAttachmentSizeRef.current - (file.originFileObj?.size || 0));
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handlePreview = (file) => {
    if (isImageFile(file)) {
      setPreviewImage(file.url || file.thumbUrl);
      setPreviewOpen(true);
    } else {
      window.open(file.url || file.thumbUrl, '_blank', 'noopener');
    }
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const newFiles = fileList.filter((f) => f.originFileObj).map((f) => f.originFileObj);
      const { referringDoctorName, childDob, ...rest } = values;
      onSubmit({
        ...rest,
        childDob: childDob ? childDob.format('YYYY-MM-DD') : undefined,
        files: newFiles,
      });
    });
  };

  return (
    <Modal
      title={isEdit ? 'Edit Referral' : 'Refer a Child'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="targetClinic" label="Target Clinic" rules={[{ required: true, message: 'Please select a target clinic' }]}>
              <Select placeholder="Select clinic" showSearch optionFilterProp="children">
                {(clinicsData?.data || []).map((c) => (
                  <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="referringDoctorName" label="Referring Doctor Name">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="referringDoctorContact"
              label="Referring Doctor Contact Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[{ required: true, message: 'Contact number is required' }, phoneRule]}
            >
              <Input inputMode="numeric" pattern="[0-9]*" maxLength={11} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="referringDoctorEmail"
              label="Referring Doctor Email"
              rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Enter a valid email address' }]}
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="childName"
              label="Child / Patient Name"
              rules={[{ required: true, message: 'Child/Patient name is required' }]}
            >
              <Input placeholder="Enter child's name" maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="childDob" label="Date of Birth" rules={[{ required: true, message: 'Date of birth is required' }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(d) => d && d > dayjs().endOf('day')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="parentName"
              label="Parent / Guardian Name"
              rules={[{ required: true, message: 'Parent/Guardian name is required' }]}
            >
              <Input placeholder="Enter parent/guardian name" maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="parentContact"
              label="Parent / Guardian Contact Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[{ required: true, message: 'Contact number is required' }, phoneRule]}
            >
              <Input inputMode="numeric" pattern="[0-9]*" maxLength={11} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="parentEmail"
              label="Parent / Guardian Email"
              rules={[{ type: 'email', message: 'Enter a valid email address' }]}
            >
              <Input placeholder="Enter parent/guardian email" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="preferredVisitTimeframe" label="Preferred Visit Timeframe">
              <Input placeholder="e.g. Next 2 weeks" maxLength={200} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="reason" label="Reason for Referral / Clinical Note">
              <Input.TextArea rows={3} placeholder="Describe the reason for referral…" maxLength={1000} />
            </Form.Item>
          </Col>
          {isEdit ? (
            <Col span={24}>
              <Form.Item
                label="Missing / Supporting Documents"
                extra="PDF, JPG or PNG · Select multiple files at once · Combined size up to 32MB"
              >
                {canManageAttachments && (
                  <Upload
                    multiple
                    showUploadList={false}
                    accept="application/pdf,image/jpeg,image/jpg,image/png"
                    beforeUpload={handleMissingDocUpload}
                  >
                    <Button icon={<UploadOutlined />} loading={addAttachmentsMutation.isPending}>
                      Upload Missing Document(s)
                    </Button>
                  </Upload>
                )}
                {(initialValues?.attachmentUrls || []).length === 0 ? (
                  <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                    No documents uploaded yet.
                  </Typography.Paragraph>
                ) : (
                  <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    {initialValues.attachmentUrls.map((url, idx) => {
                      const attachmentPath = initialValues.attachments?.[idx];
                      return (
                        <Col key={idx}>
                          <div style={{
                            position: 'relative', width: 96, border: '1px solid var(--wl-primary-pale, #f0f0f0)',
                            borderRadius: 8, padding: 6, textAlign: 'center',
                          }}
                          >
                            {canManageAttachments && attachmentPath && (
                              <Popconfirm
                                title="Remove this document?"
                                onConfirm={() => removeAttachmentMutation.mutate(attachmentPath)}
                                okText="Remove"
                                okButtonProps={{ danger: true }}
                                placement="topRight"
                              >
                                <Button
                                  shape="circle"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  loading={removeAttachmentMutation.isPending}
                                  style={{ position: 'absolute', top: -8, right: -8, zIndex: 1 }}
                                />
                              </Popconfirm>
                            )}
                            {isImageFile({ name: url }) ? (
                              <Image src={url} width={80} height={80} style={{ objectFit: 'contain' }} />
                            ) : (
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                <FilePdfOutlined style={{ fontSize: 28 }} />
                                <div style={{ fontSize: 11, marginTop: 4 }}>View</div>
                              </a>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Form.Item>
            </Col>
          ) : (
            <Col span={24}>
              <Form.Item
                label="Diagnosis / Supporting Reports"
                extra="PDF, JPG or PNG · Select multiple files at once · Click a file to preview · Combined size up to 32MB (optional)"
              >
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  beforeUpload={beforeUpload}
                  onRemove={handleRemove}
                  onPreview={handlePreview}
                  multiple
                  maxCount={5}
                  accept="application/pdf,image/jpeg,image/jpg,image/png"
                >
                  {fileList.length >= 5 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Select Files</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      <Image
        style={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
        src={previewImage}
      />
    </Modal>
  );
};

export default ReferralForm;
